import { useState, useEffect } from 'react';
import { X, Rocket, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

export default function PromotionalPopup() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Show popup after 3-4 seconds if not shown before in this session
    const hasSeenPopup = sessionStorage.getItem('hasSeenPopup');
    
    if (!hasSeenPopup) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    sessionStorage.setItem('hasSeenPopup', 'true');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Dark Blurred Background Overlay - Flexbox Container for Centering */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-md z-[100] flex items-center justify-center p-4"
            onClick={handleClose}
            data-testid="popup-overlay"
          >
            {/* Popup - Perfectly Centered */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ 
                type: 'spring', 
                damping: 25, 
                stiffness: 300,
                duration: 0.4 
              }}
              className="relative w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
              data-testid="promotional-popup"
            >
              <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
                {/* Close Button - Top Right */}
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 bg-white hover:bg-gray-100 p-2 rounded-full shadow-lg transition-all z-10 hover:scale-110"
                  aria-label="Close popup"
                  data-testid="close-popup"
                >
                  <X className="w-5 h-5 text-gray-700" />
                </button>

                {/* Header with Fire Icon */}
                <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-6 text-center relative overflow-hidden">
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIgLz48L3N2Zz4=')]"></div>
                  </div>
                  <div className="relative">
                    <div className="text-5xl mb-2 animate-bounce">🔥</div>
                    <h2 className="text-2xl font-extrabold font-heading mb-1">New Course Launch</h2>
                    <div className="bg-yellow-400 text-orange-900 inline-block px-4 py-1 rounded-full text-sm font-bold">
                      Limited Time Offer
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4 text-center">
                    Digital Research <br/>
                    <span className="gradient-text">Mastery Course</span>
                  </h3>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700">Digital Research Skills</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700">AI Tools & Techniques</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700">Online Career Growth</span>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-2xl mb-6">
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-1">Special Launch Price</p>
                      <div className="flex items-center justify-center gap-3">
                        <span className="text-2xl font-bold text-gray-400 line-through">₹15,999</span>
                        <span className="text-4xl font-extrabold text-orange-600">₹10,998</span>
                      </div>
                      <p className="text-sm text-green-600 font-semibold mt-1">Save ₹5,001!</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Link
                      to="/checkout"
                      onClick={handleClose}
                      className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-4 rounded-full font-bold text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                      data-testid="popup-enroll-button"
                    >
                      <Rocket className="w-5 h-5" />
                      Start Learning Now
                    </Link>
                    
                    <button
                      onClick={handleClose}
                      className="w-full text-gray-600 hover:text-gray-800 font-medium py-2 transition-colors"
                    >
                      Maybe Later
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
